import { Routes } from '@angular/router';
import { MainLayoutComponent } from './layout/main-layout.component';
import { AuthGuard } from './core/auth/auth.guard';
import { guestGuard } from './core/auth/guest.guard';
import { featureVisibilityGuard } from './core/guards/feature-visibility.guard';
import {
  forgetPasswordOtpCanDeactivateGuard,
  forgetPasswordOtpGuard,
  forgetPasswordSetPasswordCanDeactivateGuard,
  forgetPasswordSetPasswordGuard,
} from './features/auth/forget-password/forget-password.guards';
import {
  signUpDisabledGuard,
  signUpOtpGuard,
  signUpPasswordCanDeactivateGuard,
  signUpPasswordGuard,
} from './features/auth/sign-up/sign-up.guards';

export const routes: Routes = [
  {
    path: 'onboarding',
    loadComponent: () => import('./features/onboarding/onboarding.component').then((m) => m.OnboardingComponent),
  },
  {
    path: 'auth',
    canActivate: [guestGuard],
    canActivateChild: [guestGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'sign-up',
        canActivate: [signUpDisabledGuard],
        loadComponent: () => import('./features/auth/sign-up/sign-up.component').then((m) => m.SignUpComponent),
        children: [
          { path: 'credential', redirectTo: '', pathMatch: 'full' },
          {
            path: '',
            loadComponent: () =>
              import('./features/auth/sign-up/components/credential/credential.component').then(
                (m) => m.CredentialComponent
              ),
          },
          {
            path: 'credential/:refCode',
            loadComponent: () =>
              import('./features/auth/sign-up/components/credential/credential.component').then(
                (m) => m.CredentialComponent
              ),
          },
          {
            path: 'otp',
            canActivate: [signUpOtpGuard],
            loadComponent: () =>
              import('./features/auth/sign-up/components/otp/otp.component').then((m) => m.OtpComponent),
          },
          {
            path: 'password',
            canActivate: [signUpPasswordGuard],
            canDeactivate: [signUpPasswordCanDeactivateGuard],
            loadComponent: () =>
              import('./features/auth/sign-up/components/set-password/set-password.component').then(
                (m) => m.SetPasswordComponent
              ),
          },
        ],
      },
      {
        path: 'forget-password',
        loadComponent: () =>
          import('./features/auth/forget-password/forget-password.component').then((m) => m.ForgetPasswordComponent),
        children: [
          { path: '', redirectTo: 'mobile-input', pathMatch: 'full' },
          {
            path: 'mobile-input',
            loadComponent: () =>
              import('./features/auth/forget-password/components/mobile-input/mobile-input.component').then(
                (m) => m.MobileInputComponent
              ),
          },
          {
            path: 'otp',
            canActivate: [forgetPasswordOtpGuard],
            canDeactivate: [forgetPasswordOtpCanDeactivateGuard],
            loadComponent: () =>
              import('./features/auth/forget-password/components/otp/otp.component').then((m) => m.OtpComponent),
          },
          {
            path: 'set-password',
            canActivate: [forgetPasswordSetPasswordGuard],
            canDeactivate: [forgetPasswordSetPasswordCanDeactivateGuard],
            loadComponent: () =>
              import('./features/auth/forget-password/components/set-password/set-password.component').then(
                (m) => m.SetPasswordComponent
              ),
          },
        ],
      },
    ],
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'announcements',
        loadComponent: () =>
          import('./features/announcements/announcements.component').then((m) => m.AnnouncementsComponent),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'announcementsVisible' },
      },
      {
        path: 'offers/advance-payment',
        loadComponent: () =>
          import('./features/offers/advance-payment/advance-payment.component').then((m) => m.AdvancePaymentComponent),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'offersVisible' },
      },
      {
        path: 'offers',
        loadComponent: () => import('./features/offers/offers.component').then((m) => m.OffersComponent),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'offersVisible' },
      },
      {
        path: 'plans',
        loadChildren: () => import('./features/plans/plans.routes').then((m) => m.PLAN_ROUTES),
      },
      {
        path: 'profile',
        loadChildren: () => import('./features/profile/profile.routes').then((m) => m.PROFILE_ROUTES),
      },
      {
        path: 'other-accounts',
        loadComponent: () =>
          import('./features/other-accounts/other-accounts.component').then((m) => m.OtherAccountsComponent),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'otherAccountsVisible' },
      },
      {
        path: 'other-accounts/add',
        loadComponent: () =>
          import('./features/other-accounts/add-account/add-account-page.component').then(
            (m) => m.AddAccountPageComponent
          ),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'otherAccountsVisible' },
      },
      {
        path: 'recharge',
        loadChildren: () => import('./features/recharge/recharge.routes').then((m) => m.RechargeRoutes),
      },
      {
        path: 'mobile-recharge',
        loadChildren: () => import('./features/topup/topup.routes').then((m) => m.TopupRoutes),
        title: 'Mobile Recharge',
      },
      {
        path: 'utility-bill',
        loadChildren: () => import('./features/utility-bill/utility-bill.routes').then((m) => m.UtilityBillRoutes),
        title: 'Utility Bill Payment',
      },
      {
        path: 'support',
        loadChildren: () => import('./features/support/support.routes').then((m) => m.supportRoutes),
      },
      {
        path: 'notifications',
        loadChildren: () => import('./features/notification/notification.routes').then((m) => m.notificationRoutes),
      },
      {
        path: 'transactions',
        loadChildren: () => import('./features/transaction/transaction.routes').then((m) => m.transactionRoutes),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'walletVisible' },
      },
      {
        path: 'referral',
        loadComponent: () => import('./features/referral/referral.component').then((m) => m.ReferralComponent),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'loyaltyOptionVisible' },
      },
      {
        path: 'my-plan',
        loadChildren: () => import('./features/my-plan/my-plan.routes').then((m) => m.MY_PLAN_ROUTES),
      },
      {
        path: 'usages',
        loadChildren: () => import('./features/usages/usages.routes').then((m) => m.USAGES_ROUTES),
      },
      {
        path: 'trending-now',
        loadChildren: () => import('./features/trending-now/trending-now.routes').then((m) => m.TRENDING_NOW_ROUTES),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'ullashTrendingContentVisible' },
      },
      {
        path: 'other-service',
        loadChildren: () => import('./features/other-service/other-service.routes').then((m) => m.OTHER_SERVICE_ROUTES),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'otherServicesVisible' },
      },
      {
        path: 'loyalty',
        loadChildren: () => import('./features/loyalty/loyalty.routes').then((m) => m.LOYALTY_ROUTES),
        canActivate: [featureVisibilityGuard],
        data: { requireFeature: 'loyaltyOptionVisible' },
      },
    ],
  },
  {
    path: 'invoice/:identity',
    loadComponent: () => import('./features/invoice-view/invoice-view.component').then((m) => m.InvoiceViewComponent),
  },
];
